import { Cookies } from '@/enums/cookies.enum';
import type {
    ForgotPasswordInterface,
    LoginPayload,
    RegisterPayload,
    ResetPasswordInterface,
    Session,
} from '@/types/api.types';

import { useApplicationStore } from '@/stores/application.store';
import { useUserStore } from '@/stores/user.store';
import axios from '@/services/axios.service';
import router from '@/router';
import usePopup from '@/composables/usePopup';
import apiService from '@/services/api.service';
import useEventBus from '@/composables/useEventBus';
import cookieService from '@/services/cookie.service';
import languageService from '@/services/language.service';
import trackingService from './tracking.service';
import { GoogleTagManagerSubjectEnums } from '@/enums/tracking.enum';
import { UserTypes } from '@/types/user.types';

const popup = usePopup();
const eventBus = useEventBus();

export default {
    SOCIAL_AUTH_OPTIONS: {
        google: 'google',
        facebook: 'facebook-f',
        linkedin: 'linkedin-in',
        apple: 'apple',
    },

    SOCIAL_AUTH_NAMES: {
        google: 'Google',
        facebook: 'Facebook',
        linkedin: 'LinkedIn',
        apple: 'Apple',
    },

    async isAuthenticated(): Promise<boolean> {
        const data = await this.getSession();

        if (!data) {
            return false;
        }

        this.setSessionAndUser(data);

        return true;
    },

    async getSession(id = ''): Promise<Session | void> {
        if (!id) {
            id = cookieService.get(Cookies.Session) as string;

            if (!id) {
                return;
            }
        }

        const result = await apiService.get(`sessions/${id}`);

        if (result?.status === 200) {
            return result.data as Session;
        } else {
            this.logout();
        }
    },

    hasLocalSession(): boolean {
        return Boolean(cookieService.get(Cookies.Session));
    },

    async register(payload: RegisterPayload) {
        const { status, data } = await apiService.post('actions/register', payload);

        if (status === 201) {
            trackingService.sendGtm(GoogleTagManagerSubjectEnums.AccountRegistration, {
                email: payload.email,
                phone: payload.phone,
                first_name: payload.first_name,
                last_name: payload.last_name,
            });
        }

        const user: UserTypes | undefined = data?.user;
        const session: Session | undefined = data?.session;

        if (session) this.setSessionAndUser(session);

        return {
            user,
            error: status !== 201 ? (data as string) : '',
            alreadyRegistered: status === 409,
        };
    },

    setSessionAndUser(session: Session) {
        const userStore = useUserStore();
        axios.defaults.headers.common['X-session'] = session.id;
        cookieService.set(Cookies.Session, session.id);
        userStore.updateSession(session);
        userStore.updateAccounts();
        languageService.setUserLanguage();
        eventBus.emit('login', session);
    },

    async logout(redirect = false): Promise<void> {
        const appStore = useApplicationStore();
        const url = appStore.info?.tenant.logoutUrl;
        router.push({ name: 'logout', query: { redirect: redirect ? url : undefined } });
    },

    async login(payload: LoginPayload): Promise<Session | undefined> {
        const { status, data } = await apiService.post('sessions', payload);

        if (status === 201) {
            this.setSessionAndUser(data);

            return data as Session;
        } else {
            popup.toast.error(data);
        }
    },

    async resetPassword(d: ResetPasswordInterface): Promise<any> {
        const { status, data } = await apiService.post('actions/set-password', d);

        if (status === 201) {
            this.setSessionAndUser(data);
        }

        return {
            status,
            data,
        };
    },

    async forgotPassword(d: ForgotPasswordInterface): Promise<{ status: number }> {
        try {
            const { status } = await apiService.post('actions/forgot-password', d);

            return { status };
        } catch (e) {
            console.error(e);

            return Promise.reject(e);
        }
    },

    redirectSocialAuth(option: keyof typeof this.SOCIAL_AUTH_OPTIONS, redirectUrl?: string) {
        // Make the oauth redirect work locally
        const isProd = import.meta.env.PROD;
        const baseUrl = isProd ? '/api/' : apiService.API_URL;

        const applicationStore = useApplicationStore();
        const searchParams = new URLSearchParams();
        if (redirectUrl) {
            searchParams.append('redirectUrl', redirectUrl);
        }
        applicationStore.updateOAuthLink(router.currentRoute.value.fullPath);

        return window.location.assign(`${baseUrl}oauth/${option}?${searchParams.size ? searchParams : ''}`);
    },
};
